import * as React from 'react';
import '@progress/kendo-theme-bootstrap/dist/all.css';
import StringResourceManager, { GetString } from '../StringResourceManager';
import { Dialog } from '@progress/kendo-react-dialogs';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { Grid, createStyles, makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import _ from 'lodash';
import NodeSelection from '../NodeSelection';
import {
  SendToDivisionCommandInfo,
  SendToLanesCommandInfo,
  SendToNodeGroupsCommandInfo,
  SendToSiteNumbersCommandInfo,
} from '../../Interfaces/Commands/CommandInfo';
import {
  sendToDivisionCommand,
  sendToLanesCommand,
  sendToNodeGroupsCommand,
  sendToSiteNumberCommand,
} from '../../store/Assets/AssetsActions';
import { fetchAccessControlFilePath } from '../../store/AccessControl/AccessControlActions';
import { ApplicationState } from '../../store';
import { Checkbox, CheckboxChangeEvent } from '@progress/kendo-react-inputs';

const useStyles = makeStyles(theme =>
  createStyles({
    gridAlign: {
      display: 'flex',
    },
    margin: {
      margin: theme.spacing(1),
    },
    dialogLabel: {
      fontSize: '1.1rem',
      display: 'inline',
    },
    arrowIcon: {
      height: '50px',
      weight: '50px',
    },
    iconGrid: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      margin: 10,
    },
    listBoxGrid: {
      width: '250px',
      marginRight: '10px',
    },
    listBox: {
      border: '1px solid #000000',
    },
    dropdownStyle: {
      marginTop: '25px',
      marginLeft: '25px',
    },
    buttonStyle: {
      alignItems: 'flex-end',
      margin: '25px',
    },
    enableButton: {
      width: '180px',
      border: '1px solid #000000',
    },
    disableButton: {
      width: '180px',
    },
    checkBoxStyle: { marginRight: '15px' },
  })
);

interface Props {
  handleCloseDialog: (open: boolean) => void;
  setDisplayCommandSend: (open: boolean) => void;
  fileName: string;
}

const LaneSelectionDialog: React.FC<Props> = ({
  handleCloseDialog,
  setDisplayCommandSend: setDisplayCommandSend,
  fileName,
}): React.ReactElement => {
  const classes = useStyles();

  const dispatch: Dispatch<any> = useDispatch();

  const uploadAccessControlFilePath: string = useSelector(
    (state: ApplicationState) => state.accessControlReducer.filePath
  );

  const laneNumberSelectionFilter: string[] = useSelector(
    (state: ApplicationState) =>
      state.accessControlReducer.laneNumberSelectionFilter
  );

  const [deleteUsers, setDeleteUsers] = React.useState(true);

  const [passwordExpiry, setPasswordExpiry] = React.useState(false);

  const [barcodeExpiry, setBarcodeExpiry] = React.useState(false);

  const handleClose = () => {
    handleCloseDialog(false);
  };

  const boolToNum = (bool: boolean) => {
    return bool ? 1 : 0;
  };

  const handleParameters = () => {
    return `<ConfigAccessControl><FilePath>${uploadAccessControlFilePath}</FilePath><DeleteUsers>${boolToNum(
      deleteUsers
    )}</DeleteUsers><PasswordExpiry>${boolToNum(
      passwordExpiry
    )}</PasswordExpiry><BarcodeExpiry>${boolToNum(
      barcodeExpiry
    )}</BarcodeExpiry></ConfigAccessControl>`;
  };

  const setSendToSiteNumberCommand = (
    command: SendToSiteNumbersCommandInfo
  ) => {
    let commandInfo: SendToSiteNumbersCommandInfo = {
      Division: command.Division,
      SiteNumbers: command.SiteNumbers,
      CommandInfo: {
        Command: command.CommandInfo.Command,
        Parameters: handleParameters(),
      },
    };
    sendToSiteNumberCommand(dispatch, commandInfo);
    handleCloseDialog(false);
    setDisplayCommandSend(true);
  };

  const setSendToNodeGroupsCommand = (command: SendToNodeGroupsCommandInfo) => {
    let commandInfo: SendToNodeGroupsCommandInfo = {
      Division: command.Division,
      SiteNumber: command.SiteNumber,
      NodeGroups: command.NodeGroups,
      CommandInfo: {
        Command: command.CommandInfo.Command,
        Parameters: handleParameters(),
      },
    };

    sendToNodeGroupsCommand(dispatch, commandInfo);
    handleCloseDialog(false);
    setDisplayCommandSend(true);
  };

  const setSendToLanesCommand = (command: SendToLanesCommandInfo) => {
    let commandInfo: SendToLanesCommandInfo = {
      Division: command.Division,
      SiteNumber: command.SiteNumber,
      NodeGroup: command.NodeGroup,
      LaneNumbers: command.LaneNumbers,
      CommandInfo: {
        Command: command.CommandInfo.Command,
        Parameters: handleParameters(),
      },
    };

    sendToLanesCommand(dispatch, commandInfo);
    handleCloseDialog(false);
    setDisplayCommandSend(true);
  };

  const setSendToDivisionCommand = (command: SendToDivisionCommandInfo) => {
    let commandInfo: SendToDivisionCommandInfo = {
      Divisions: command.Divisions,
      CommandInfo: {
        Command: command.CommandInfo.Command,
        Parameters: handleParameters(),
      },
    };

    sendToDivisionCommand(dispatch, commandInfo);
    handleCloseDialog(false);
    setDisplayCommandSend(true);
  };

  React.useEffect(() => {
    fetchAccessControlFilePath(dispatch, fileName);
  }, []);

  const deleteUserOnChange = (e: CheckboxChangeEvent) => {
    setDeleteUsers(e.value);
  };

  const passwordExpireOnChange = (e: CheckboxChangeEvent) => {
    setPasswordExpiry(e.value);
  };

  const barcodeExpireOnChange = (e: CheckboxChangeEvent) => {
    setBarcodeExpiry(e.value);
  };

  return (
    <>
      <Dialog
        style={{ zIndex: 10000 }}
        height="75vh"
        minWidth="55vw"
        title={
          <StringResourceManager
            variant="body1"
            style="commandLabel"
            stringKey={'SEND_CSV_TO_LANE'}
            gutterBottom={true}
          />
        }
        onClose={handleClose}
      >
        <Grid>
          <Checkbox
            value={deleteUsers}
            label={GetString('DELETE_EXISTING_USER_LABEL')}
            onChange={deleteUserOnChange}
            className={classes.checkBoxStyle}
          />
          <Checkbox
            value={passwordExpiry}
            onChange={passwordExpireOnChange}
            label={GetString('PASSWORD_EXPIRE_LABEL')}
            className={classes.checkBoxStyle}
          />
          <Checkbox
            value={barcodeExpiry}
            onChange={barcodeExpireOnChange}
            label={GetString('BARCODE_EXPIRE_LABEL')}
            className={classes.checkBoxStyle}
          />
          <NodeSelection
            command={'CONFIG_ACCESS_CONTROL'}
            commandDropdownlistEnable={false}
            setSendToSiteNumberCommand={setSendToSiteNumberCommand}
            setSendToNodeGroupsCommand={setSendToNodeGroupsCommand}
            setSendToLanesCommand={setSendToLanesCommand}
            buttonTitleKey="SEND_BUTTON"
            setSendToDivisionCommand={setSendToDivisionCommand}
            laneNumberSelectionFilter={laneNumberSelectionFilter}
          />
        </Grid>
      </Dialog>
    </>
  );
};

export default LaneSelectionDialog;
